// src/pages/Comptabilite.jsx
// ──────────────────────────────────────────────────────────────
//  LOGIQUE FINANCIÈRE :
//   ⚡ Revenu Total Énergie = Transactions (inchangé, renommé)
//   ➕ Recettes             = Revenue (saisies manuelles)
//   💰 Revenu Total         = Énergie + Recettes  (cumulé dynamiquement)
//   📊 Solde Net            = Revenu Total − Dépenses  (vrai solde)
//   ✅ Bilan Cumulé Toutes Années : /accounting/stats/all-time
//      Se met à jour automatiquement dès qu'une nouvelle année apparaît
//      (2024 + 2025 + 2026 + 2027 + ... sans limite).
// ──────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from 'react';
import {TrendingUp, TrendingDown, PlusCircle, Trash2, Edit3, Download, FileText, X, Save, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Wallet, BarChart2, Calendar, Tag, Eye, AlertTriangle, Loader, RefreshCw, Search, Percent, Calculator, User, AlignLeft, CheckCircle, Info, Clock, Hash, Lock, BadgeDollarSign, Layers
} from 'lucide-react';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';
import { api } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import { useAuth } from '../context/AuthContext';

// ─── Catégories ────────────────────────────────────────────
const CATEGORIES = [
  { value: 'transport',    label: '🚗 Transport',      color: '#3B82F6' },
  { value: 'materiel',     label: '🔧 Matériel',       color: '#F59E0B' },
  { value: 'salaire',      label: '👷 Salaire/Prime',  color: '#8B5CF6' },
  { value: 'maintenance',  label: '⚙️ Maintenance',    color: '#10B981' },
  { value: 'fourniture',   label: '📦 Fournitures',    color: '#EC4899' },
  { value: 'communication',label: '📡 Communication',  color: '#06B6D4' },
  { value: 'divers',       label: '📋 Divers',         color: '#6B7280' },
  { value: 'autre',        label: '✏️ Autre',          color: '#A855F7' },
];

const REVENUE_CATEGORIES = [
  { value: 'vente',         label: '💰 Vente',         color: '#10B981' },
  { value: 'service',       label: '🛠️ Service',       color: '#3B82F6' },
  { value: 'abonnement',    label: '🔄 Abonnement',    color: '#8B5CF6' },
  { value: 'subvention',    label: '🏛️ Subvention',    color: '#F59E0B' },
  { value: 'remboursement', label: '↩️ Remboursement', color: '#06B6D4' },
  { value: 'divers',        label: '📋 Divers',        color: '#6B7280' },
  { value: 'autre',         label: '✏️ Autre',         color: '#A855F7' },
];

const getCat    = (v) => CATEGORIES.find(c => c.value === v)       || { value: v, label: `✏️ ${v}`, color: '#A855F7' };
const getRevCat = (v) => REVENUE_CATEGORIES.find(c => c.value === v) || { value: v, label: `✏️ ${v}`, color: '#A855F7' };

const MONTHS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const MFULL  = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const fmt  = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' FCFA';
const fmtS = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(0) + 'k';
  return String(Math.round(n));
};
const today = () => new Date().toISOString().slice(0, 10);

// ═══════════════════════════════════════════════════════════
const Comptabilite = () => {
  const { hasPermission, isAdmin } = usePermissions();
  const { user } = useAuth();
  const canViewAccounting = hasPermission(PERMISSIONS.VIEW_ACCOUNTING);
  const canManageExpenses = hasPermission(PERMISSIONS.MANAGE_EXPENSES);
  const canDelete         = isAdmin;
  const canExport         = hasPermission(PERMISSIONS.EXPORT_REPORTS);

  const [activeTab, setActiveTab] = useState('depenses');
  const [expenses,  setExpenses]  = useState([]);
  const [revenues,  setRevenues]  = useState([]);
  const [stats,     setStats]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [chartData, setChartData] = useState([]);
  const [catData,   setCatData]   = useState([]);
  const [revCatData,setRevCatData]= useState([]);

  // ✅ Bilan cumulé sur TOUTES les années (indépendant du sélecteur d'année)
  const [allTimeStats,   setAllTimeStats]   = useState(null);
  const [loadingAllTime, setLoadingAllTime] = useState(true);

  // Filtres
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState('all');
  const [yearFilter,  setYearFilter]  = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState('all');
  const [page,        setPage]        = useState(1);
  const [PER_PAGE,    setPERPAGE]     = useState(15);

  // Modals
  const [showExpForm,     setShowExpForm]     = useState(false);
  const [editExpTarget,   setEditExpTarget]   = useState(null);
  const [deleteExpTarget, setDeleteExpTarget] = useState(null);
  const [showRevForm,     setShowRevForm]     = useState(false);
  const [editRevTarget,   setEditRevTarget]   = useState(null);
  const [deleteRevTarget, setDeleteRevTarget] = useState(null);
  const [showDetail,      setShowDetail]      = useState(false);
  const [detailItem,      setDetailItem]      = useState(null);
  const [detailType,      setDetailType]      = useState('depense');
  const [saving,          setSaving]          = useState(false);

  const [expForm, setExpForm] = useState({ date: today(), category: 'transport', description: '', amount: '', beneficiary: '', notes: '', customCategory: '' });
  const [revForm, setRevForm] = useState({ date: today(), category: 'vente',     description: '', amount: '', source: '',      notes: '', customCategory: '' });

  const [notif, setNotif] = useState(null);
  const notify = (type, msg) => { setNotif({ type, msg }); setTimeout(() => setNotif(null), 3500); };

  useEffect(() => { loadAll(); }, [yearFilter]);
// ✅ Le bilan cumulé suit maintenant le sélecteur d'année : il s'arrête
// toujours à l'année choisie, pas forcément à l'année réelle.
  useEffect(() => { loadAllTimeStats(); }, [yearFilter]);
  // ✅ Le bilan cumulé toutes années se charge une seule fois (indépendant de yearFilter),
  // et se rafraîchit avec le bouton "Actualiser" via loadAll()
  useEffect(() => { setPage(1); setSearch(''); setCatFilter('all'); setMonthFilter('all'); }, [activeTab]);

  const customCatsInExpenses = useMemo(() => {
    const known = new Set(CATEGORIES.map(c => c.value));
    return [...new Set(expenses.filter(e => !known.has(e.category) && e.category).map(e => e.category))].sort();
  }, [expenses]);
  const customCatsInRevenues = useMemo(() => {
    const known = new Set(REVENUE_CATEGORIES.map(c => c.value));
    return [...new Set(revenues.filter(r => !known.has(r.category) && r.category).map(r => r.category))].sort();
  }, [revenues]);

  const filteredExp = useMemo(() => expenses.filter(e => {
    const ms = !search || e.description?.toLowerCase().includes(search.toLowerCase()) || e.beneficiary?.toLowerCase().includes(search.toLowerCase());
    const mc = catFilter === 'all' ? true : catFilter === 'autre'
      ? !new Set(CATEGORIES.filter(c => c.value !== 'autre').map(c => c.value)).has(e.category)
      : e.category === catFilter;
    const mm = monthFilter === 'all' || new Date(e.date).getMonth() === +monthFilter;
    return ms && mc && mm;
  }).sort((a,b) => new Date(b.date) - new Date(a.date)), [expenses, search, catFilter, monthFilter]);

  const filteredRev = useMemo(() => revenues.filter(r => {
    const ms = !search || r.description?.toLowerCase().includes(search.toLowerCase()) || r.source?.toLowerCase().includes(search.toLowerCase());
    const mc = catFilter === 'all' ? true : catFilter === 'autre'
      ? !new Set(REVENUE_CATEGORIES.filter(c => c.value !== 'autre').map(c => c.value)).has(r.category)
      : r.category === catFilter;
    const mm = monthFilter === 'all' || new Date(r.date).getMonth() === +monthFilter;
    return ms && mc && mm;
  }).sort((a,b) => new Date(b.date) - new Date(a.date)), [revenues, search, catFilter, monthFilter]);

  if (!canViewAccounting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-12 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Accès refusé</h2>
          <p className="text-gray-500 text-sm">Vous n'avez pas la permission d'accéder à la comptabilité.</p>
        </div>
      </div>
    );
  }

  const loadAll = async () => {
    setLoading(true);
    try {
      const [expRes, revRes, statRes] = await Promise.all([
        api.get('/accounting/expenses', { params: { year: yearFilter, limit: 500 } }),
        api.get('/accounting/revenues', { params: { year: yearFilter, limit: 500 } }),
        api.get('/accounting/stats',    { params: { year: yearFilter } }),
      ]);
      const expData = expRes.data?.items || [];
      const revData = revRes.data?.items || [];
      setExpenses(expData);
      setRevenues(revData);
      setStats(statRes.data);
      buildCharts(expData, revData, statRes.data);
    } catch { notify('error', 'Erreur de chargement'); }
    finally { setLoading(false); }
  };

  // ✅ Charge le bilan cumulé sur toutes les années présentes en base
const loadAllTimeStats = async () => {
  setLoadingAllTime(true);
  try {
    const res = await api.get('/accounting/stats/all-time', { params: { year: yearFilter } });
    setAllTimeStats(res.data);
  } catch { /* silencieux : la carte "cumulé" reste optionnelle */ }
  finally { setLoadingAllTime(false); }
};

  const buildCharts = (exps, revs, st) => {
    const monthly = MONTHS.map((m, i) => ({
      month:    m,
      energie:  st?.monthly_chart?.[i]?.energie  || 0,
      recettes: st?.monthly_chart?.[i]?.recettes || 0,
      depenses: st?.monthly_chart?.[i]?.depenses || 0,
      solde:    st?.monthly_chart?.[i]?.solde    || 0,
    }));
    setChartData(monthly);

    const catMap = {};
    exps.forEach(e => { catMap[e.category] = (catMap[e.category] || 0) + +e.amount; });
    setCatData(Object.entries(catMap).map(([v,t]) => ({...getCat(v), value:v, total:t})).filter(c=>c.total>0).sort((a,b)=>b.total-a.total));

    const revMap = {};
    revs.forEach(r => { revMap[r.category] = (revMap[r.category] || 0) + +r.amount; });
    setRevCatData(Object.entries(revMap).map(([v,t]) => ({...getRevCat(v), value:v, total:t})).filter(c=>c.total>0).sort((a,b)=>b.total-a.total));
  };

  const totalEnergie   = stats?.total_energie   || 0;
  const totalRecettes  = stats?.total_recettes  || 0;
  const totalRevenue   = stats?.total_revenue   || 0;
  const totalExpenses  = stats?.total_expenses  || 0;
  const netBalance     = stats?.net_balance     || 0;
  const expenseRatio   = stats?.expense_ratio   || 0;
  const avgExp         = stats?.monthly_avg_expense || 0;
  const curM           = new Date().getMonth();
  const monthEnergie   = stats?.current_month_energie       || 0;
  const monthRecettes  = stats?.current_month_recettes      || 0;
  const monthRevTotal  = stats?.current_month_revenue_total || 0;
  const monthExp       = stats?.current_month_expense       || 0;

  // ✅ Bilan cumulé toutes années
  const allTime          = allTimeStats?.all_time || {};
  const allTimeYears     = allTimeStats?.years     || [];

  const totalExpFiltered = filteredExp.reduce((s,e) => s + +e.amount, 0);
  const totalRevFiltered = filteredRev.reduce((s,r) => s + +r.amount, 0);

  const activeList = activeTab === 'depenses' ? filteredExp : filteredRev;
  const totalPages = Math.ceil(activeList.length / PER_PAGE);
  const paginated  = activeList.slice((page-1)*PER_PAGE, page*PER_PAGE);

  // ── CRUD Dépenses ──
  const openAddExp = () => {
    if (!canManageExpenses) { notify('warn', 'Permission insuffisante'); return; }
    setEditExpTarget(null);
    setExpForm({ date: today(), category: 'transport', description: '', amount: '', beneficiary: '', notes: '', customCategory: '' });
    setShowExpForm(true);
  };
  const openEditExp = (exp) => {
    if (!canManageExpenses) { notify('warn', 'Permission insuffisante'); return; }
    const isCustom = !CATEGORIES.find(c => c.value === exp.category);
    setEditExpTarget(exp);
    setExpForm({ date: exp.date?.slice(0,10)||today(), category: isCustom?'autre':exp.category,
      description: exp.description||'', amount: String(exp.amount),
      beneficiary: exp.beneficiary||'', notes: exp.notes||'',
      customCategory: isCustom?exp.category:'' });
    setShowExpForm(true);
  };
  const handleSaveExp = async () => {
    if (!expForm.description.trim() || !expForm.amount || +expForm.amount <= 0) { notify('warn', 'Description et montant requis'); return; }
    if (expForm.category === 'autre' && !expForm.customCategory.trim()) { notify('warn', 'Précisez la catégorie'); return; }
    setSaving(true);
    try {
      const payload = { ...expForm, amount: +expForm.amount, category: expForm.category==='autre'?expForm.customCategory.trim():expForm.category };
      delete payload.customCategory;
      if (editExpTarget) { await api.put(`/accounting/expenses/${editExpTarget.id}`, payload); notify('success', 'Dépense modifiée'); }
      else               { await api.post('/accounting/expenses', payload); notify('success', 'Dépense ajoutée'); }
      setShowExpForm(false); loadAll(); loadAllTimeStats();
    } catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  const confirmDeleteExp = async () => {
    if (!deleteExpTarget) return;
    try { await api.delete(`/accounting/expenses/${deleteExpTarget.id}`); notify('success', 'Dépense supprimée'); loadAll(); loadAllTimeStats(); }
    catch { notify('error', 'Erreur suppression'); }
    finally { setDeleteExpTarget(null); }
  };

  // ── CRUD Recettes ──
  const openAddRev = () => {
    if (!canManageExpenses) { notify('warn', 'Permission insuffisante'); return; }
    setEditRevTarget(null);
    setRevForm({ date: today(), category: 'vente', description: '', amount: '', source: '', notes: '', customCategory: '' });
    setShowRevForm(true);
  };
  const openEditRev = (rev) => {
    if (!canManageExpenses) { notify('warn', 'Permission insuffisante'); return; }
    const isCustom = !REVENUE_CATEGORIES.find(c => c.value === rev.category);
    setEditRevTarget(rev);
    setRevForm({ date: rev.date?.slice(0,10)||today(), category: isCustom?'autre':rev.category,
      description: rev.description||'', amount: String(rev.amount),
      source: rev.source||'', notes: rev.notes||'',
      customCategory: isCustom?rev.category:'' });
    setShowRevForm(true);
  };
  const handleSaveRev = async () => {
    if (!revForm.description.trim() || !revForm.amount || +revForm.amount <= 0) { notify('warn', 'Description et montant requis'); return; }
    if (revForm.category === 'autre' && !revForm.customCategory.trim()) { notify('warn', 'Précisez la catégorie'); return; }
    setSaving(true);
    try {
      const payload = { ...revForm, amount: +revForm.amount, category: revForm.category==='autre'?revForm.customCategory.trim():revForm.category };
      delete payload.customCategory;
      if (editRevTarget) { await api.put(`/accounting/revenues/${editRevTarget.id}`, payload); notify('success', 'Recette modifiée'); }
      else               { await api.post('/accounting/revenues', payload); notify('success', 'Recette ajoutée'); }
      setShowRevForm(false); loadAll(); loadAllTimeStats();
    } catch (e) { notify('error', e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };
  const confirmDeleteRev = async () => {
    if (!deleteRevTarget) return;
    try { await api.delete(`/accounting/revenues/${deleteRevTarget.id}`); notify('success', 'Recette supprimée'); loadAll(); loadAllTimeStats(); }
    catch { notify('error', 'Erreur suppression'); }
    finally { setDeleteRevTarget(null); }
  };

  // ── Export ──
  const handleExcel = async () => {
    if (!canExport) { notify('warn', 'Permission insuffisante'); return; }
    try {
      const res = await api.get('/accounting/expenses/export-xlsx', { params: { year: yearFilter }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), { href: url, download: `comptabilite_${yearFilter}.xlsx` }).click();
      URL.revokeObjectURL(url); notify('success', 'Export Excel téléchargé');
    } catch { notify('error', 'Erreur export Excel'); }
  };
  const handlePDF = async () => {
    if (!canExport) { notify('warn', 'Permission insuffisante'); return; }
    try {
      const res = await api.get('/accounting/expenses/export-pdf', { params: { year: yearFilter }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), { href: url, download: `rapport_${yearFilter}.pdf` }).click();
      URL.revokeObjectURL(url); notify('success', 'Export PDF téléchargé');
    } catch { notify('error', 'Erreur export PDF'); }
  };
  const handleExcelRecettes = async () => {
    if (!canExport) { notify('warn', 'Permission insuffisante'); return; }
    try {
      const res = await api.get('/accounting/revenues/export-xlsx', { params: { year: yearFilter }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), { href: url, download: `recettes_${yearFilter}.xlsx` }).click();
      URL.revokeObjectURL(url); notify('success', 'Export Recettes Excel téléchargé');
    } catch { notify('error', 'Erreur export Excel recettes'); }
  };
  const handlePDFRecettes = async () => {
    if (!canExport) { notify('warn', 'Permission insuffisante'); return; }
    try {
      const res = await api.get('/accounting/revenues/export-pdf', { params: { year: yearFilter }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      Object.assign(document.createElement('a'), { href: url, download: `recettes_${yearFilter}.pdf` }).click();
      URL.revokeObjectURL(url); notify('success', 'Export Recettes PDF téléchargé');
    } catch { notify('error', 'Erreur export PDF recettes'); }
  };

  const openDetail = (item, type) => { setDetailItem(item); setDetailType(type); setShowDetail(true); };

  const CTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs">
        <p className="font-bold text-gray-700 mb-1">{label}</p>
        {payload.map(p => <p key={p.name} style={{color:p.color}}>{p.name}: {fmtS(p.value)} FCFA</p>)}
      </div>
    );
  };

  // ─── RENDER ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 p-4 lg:p-6">

      {/* Notification */}
      {notif && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium
          ${notif.type==='success'?'bg-green-50 border-green-200 text-green-800':notif.type==='error'?'bg-red-50 border-red-200 text-red-800':'bg-yellow-50 border-yellow-200 text-yellow-800'}`}>
          {notif.type==='success'?<CheckCircle className="w-4 h-4"/>:<AlertTriangle className="w-4 h-4"/>}
          {notif.msg}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            Comptabilité
          </h1>
          <p className="text-gray-500 text-sm mt-0.5 ml-12">Dépenses · Recettes · Bilan financier</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={yearFilter} onChange={e=>{setYearFilter(+e.target.value);setPage(1);}}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            {[2023,2024,2025,2026].map(y=><option key={y}>{y}</option>)}
          </select>
          <button onClick={()=>{loadAll();loadAllTimeStats();}} className="p-2 bg-white border border-gray-300 text-gray-500 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4"/>
          </button>
          {canExport && <>
            <button onClick={handleExcel} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium shadow-sm">
              <Download className="w-4 h-4"/>Excel
            </button>
            <button onClick={handlePDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium shadow-sm">
              <FileText className="w-4 h-4"/>PDF
            </button>
          </>}
          {canManageExpenses && <>
            <button onClick={openAddRev} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium shadow-sm">
              <PlusCircle className="w-4 h-4"/>Nouvelle recette
            </button>
            <button onClick={openAddExp} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm">
              <PlusCircle className="w-4 h-4"/>Nouvelle dépense
            </button>
          </>}
        </div>
      </div>

      {!canManageExpenses && (
        <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          <Lock className="w-4 h-4 flex-shrink-0"/>
          Vous êtes en mode <strong className="mx-1">lecture seule</strong>.
        </div>
      )}

      {/* STAT CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenu Totaux Énergie</p>
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-blue-600"/>
            </div>
          </div>
          <p className="text-xl font-extrabold text-gray-900 truncate">{fmt(totalEnergie)}</p>
          <p className="text-xs mt-1 text-blue-600 font-medium">Ce mois: {fmt(monthEnergie)}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dépenses Totales</p>
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-red-600"/>
            </div>
          </div>
          <p className="text-xl font-extrabold text-gray-900 truncate">{fmt(totalExpenses)}</p>
          <p className="text-xs mt-1 text-red-600 font-medium">Ce mois: {fmt(monthExp)}</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Solde Net</p>
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${netBalance>=0?'bg-emerald-100':'bg-orange-100'}`}>
              <Wallet className={`w-4 h-4 ${netBalance>=0?'text-emerald-600':'text-orange-600'}`}/>
            </div>
          </div>
          <p className={`text-xl font-extrabold truncate ${netBalance>=0?'text-emerald-700':'text-orange-700'}`}>{fmt(netBalance)}</p>
          <p className={`text-xs mt-1 font-medium ${netBalance>=0?'text-emerald-600':'text-orange-600'}`}>
            {netBalance>=0?'✅ Bénéfice':'⚠️ Déficit'}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ratio Dép./Rev.</p>
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Percent className="w-4 h-4 text-purple-600"/>
            </div>
          </div>
          <p className="text-xl font-extrabold text-gray-900">{expenseRatio.toFixed(1)}%</p>
          <p className="text-xs mt-1 text-gray-500 font-medium">Moy. mensuelle: {fmt(avgExp)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-emerald-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recettes</p>
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <BadgeDollarSign className="w-4 h-4 text-emerald-600"/>
            </div>
          </div>
          <p className="text-xl font-extrabold text-emerald-700 truncate">{fmt(totalRecettes)}</p>
          <p className="text-xs mt-1 text-emerald-600 font-medium">Ce mois: {fmt(monthRecettes)}</p>
        </div>

        <div className="bg-white border border-purple-200 rounded-2xl p-4 shadow-sm">
          <div className="flex items-start justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenu Totaux</p>
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-purple-600"/>
            </div>
          </div>
          <p className="text-xl font-extrabold text-purple-800 truncate">{fmt(totalRevenue)}</p>
          <p className="text-xs mt-1 text-purple-500 font-medium">Ce mois: {fmt(monthRevTotal)}</p>
        </div>
      </div>

      {/* ✅ RESULTAT D'EXPLOITATION TOUTES ANNÉES — même style que le reste de la page */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <Layers className="w-5 h-5 text-indigo-600"/>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-800">Resultat d'exploitation . Toutes les années</h2>
              <p className="text-gray-400 text-xs">
                {allTimeYears.length > 0
                  ? `${allTimeYears[0]} → ${allTimeYears[allTimeYears.length-1]} (${allTimeYears.length} année${allTimeYears.length>1?'s':''})`
                  : 'Aucune donnée'}
              </p>
            </div>
          </div>
          {loadingAllTime && <Loader className="w-4 h-4 animate-spin text-gray-400"/>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
            <p className="text-xs text-blue-500 mb-1"> Recette Énergetique</p>
            <p className="text-lg font-extrabold text-blue-700 truncate">{fmt(allTime.total_energie)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
            <p className="text-xs text-emerald-500 mb-1">Recettes Forfaitaires</p>
            <p className="text-lg font-extrabold text-emerald-700 truncate">{fmt(allTime.total_recettes)}</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
            <p className="text-xs text-purple-500 mb-1">Total Recettes</p>
            <p className="text-lg font-extrabold text-purple-700 truncate">{fmt(allTime.total_revenue)}</p>
          </div>
          <div className="bg-red-50 border border-red-100 rounded-xl p-3">
            <p className="text-xs text-red-500 mb-1">Total Charge</p>
            <p className="text-lg font-extrabold text-red-700 truncate">{fmt(allTime.total_expenses)}</p>
          </div>
          <div className={`rounded-xl p-3 border ${(allTime.net_balance||0)>=0 ? 'bg-emerald-50 border-emerald-100' : 'bg-orange-50 border-orange-100'}`}>
            <p className={`text-xs mb-1 ${(allTime.net_balance||0)>=0 ? 'text-emerald-500':'text-orange-500'}`}>Resultat Net</p>
            <p className={`text-lg font-extrabold truncate ${(allTime.net_balance||0)>=0 ? 'text-emerald-700':'text-orange-700'}`}>{fmt(allTime.net_balance)}</p>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          Ce bilan s'additionne automatiquement dès qu'une nouvelle année de données apparaît.
        </p>
      </div>

      {/* GRAPHIQUES */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-1 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-blue-600"/>
            Énergie · Recettes · Dépenses — {yearFilter}
          </h3>
          <p className="text-xs text-gray-400 mb-4">Barres : ⚡ Revenu Énergie (bleu) + ➕ Recettes (vert) + 💸 Dépenses (rouge)</p>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={chartData} barGap={2} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
              <XAxis dataKey="month" tick={{fill:'#94A3B8',fontSize:11}}/>
              <YAxis tickFormatter={fmtS} tick={{fill:'#94A3B8',fontSize:11}}/>
              <Tooltip content={<CTooltip/>}/>
              <Legend wrapperStyle={{fontSize:11}}/>
              <Bar dataKey="energie"  name="⚡ Énergie"   fill="#3B82F6" radius={[4,4,0,0]}/>
              <Bar dataKey="recettes" name="➕ Recettes"  fill="#10B981" radius={[4,4,0,0]}/>
              <Bar dataKey="depenses" name="💸 Dépenses"  fill="#EF4444" radius={[4,4,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <div className="flex gap-2 mb-3">
            <button onClick={()=>setActiveTab('depenses')}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeTab==='depenses'?'bg-red-100 text-red-700':'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              Dépenses
            </button>
            <button onClick={()=>setActiveTab('recettes')}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${activeTab==='recettes'?'bg-emerald-100 text-emerald-700':'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
              Recettes
            </button>
          </div>
          <h3 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
            <Tag className={`w-4 h-4 ${activeTab==='depenses'?'text-red-500':'text-emerald-600'}`}/>
            Répartition {activeTab}
          </h3>
          {(() => {
            const data = activeTab === 'depenses' ? catData : revCatData;
            return data.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Aucune entrée</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={130}>
                  <PieChart>
                    <Pie data={data} dataKey="total" cx="50%" cy="50%" outerRadius={55} innerRadius={28}>
                      {data.map((c,i) => <Cell key={i} fill={c.color}/>)}
                    </Pie>
                    <Tooltip formatter={v=>fmt(v)} contentStyle={{borderRadius:8,border:'1px solid #e2e8f0',fontSize:11}}/>
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2 max-h-24 overflow-y-auto">
                  {data.map((c,i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-0.5">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:c.color}}/>
                        <span className="text-gray-600">{c.label}</span>
                      </span>
                      <span className="text-gray-500 font-mono">{fmt(c.total)}</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Courbe solde net */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-6">
        <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600"/>
          Évolution du Solde Net mensuel (Revenu Total − Dépenses)
        </h3>
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
            <XAxis dataKey="month" tick={{fill:'#94A3B8',fontSize:11}}/>
            <YAxis tickFormatter={fmtS} tick={{fill:'#94A3B8',fontSize:11}}/>
            <Tooltip content={<CTooltip/>}/>
            <Area type="monotone" dataKey="solde" name="Solde Net" stroke="#8B5CF6" fill="url(#sg)" strokeWidth={2}/>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Onglets tableau */}
      <div className="flex gap-1 mb-4 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-sm">
        {['depenses','recettes'].map(tab => (
          <button key={tab} onClick={()=>{setActiveTab(tab);setPage(1);}}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all
              ${activeTab===tab
                ? (tab==='depenses'?'bg-red-600 text-white':'bg-emerald-600 text-white')
                : 'text-gray-500 hover:bg-gray-100'}`}>
            {tab==='depenses'?<TrendingDown className="w-4 h-4"/>:<TrendingUp className="w-4 h-4"/>}
            {tab==='depenses'?'Dépenses':'Recettes'}
            <span className={`ml-1 text-xs px-2 py-0.5 rounded-full font-bold
              ${activeTab===tab
                ? (tab==='depenses'?'bg-red-700 text-red-100':'bg-emerald-700 text-emerald-100')
                : 'bg-gray-100 text-gray-500'}`}>
              {tab==='depenses'?filteredExp.length:filteredRev.length}
            </span>
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input type="text" placeholder="Rechercher..." value={search}
              onChange={e=>{setSearch(e.target.value);setPage(1);}}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
          </div>
          <select value={catFilter} onChange={e=>{setCatFilter(e.target.value);setPage(1);}}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            <option value="all">Toutes catégories</option>
            {(activeTab==='depenses'?CATEGORIES:REVENUE_CATEGORIES).filter(c=>c.value!=='autre').map(c=>(
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
            {activeTab==='depenses' && customCatsInExpenses.length>0 && (
              <optgroup label="── Personnalisées ──">
                {customCatsInExpenses.map(v=><option key={v} value={v}>✏️ {v}</option>)}
              </optgroup>
            )}
            {activeTab==='recettes' && customCatsInRevenues.length>0 && (
              <optgroup label="── Personnalisées ──">
                {customCatsInRevenues.map(v=><option key={v} value={v}>✏️ {v}</option>)}
              </optgroup>
            )}
            <option value="autre">✏️ Autres (personnalisées)</option>
          </select>
          <select value={monthFilter} onChange={e=>{setMonthFilter(e.target.value);setPage(1);}}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
            <option value="all">Tous les mois</option>
            {MONTHS.map((m,i)=><option key={i} value={i}>{MFULL[i]}</option>)}
          </select>
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-gray-500">{activeList.length} résultat(s)</span>
            <span className={`px-3 py-1.5 border rounded-full text-xs font-bold
              ${activeTab==='depenses'?'bg-red-50 border-red-200 text-red-700':'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
              {activeTab==='depenses'?fmt(totalExpFiltered):fmt(totalRevFiltered)}
            </span>
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b border-gray-200 ${activeTab==='depenses'?'bg-red-50':'bg-emerald-50'}`}>
              <tr>
                {(activeTab==='depenses'
                  ? ['Date','Catégorie','Description','Bénéficiaire','Montant','Actions']
                  : ['Date','Catégorie','Description','Source','Montant','Actions']
                ).map(h=><th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="py-16 text-center"><Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto"/></td></tr>
              ) : paginated.length === 0 ? (
                <tr><td colSpan={6} className="py-16 text-center text-gray-400 text-sm">Aucune {activeTab==='depenses'?'dépense':'recette'} trouvée</td></tr>
              ) : paginated.map(item => {
                const isExp = activeTab === 'depenses';
                const cat   = isExp ? getCat(item.category) : getRevCat(item.category);
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {new Date(item.date).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border"
                        style={{background:cat.color+'18',borderColor:cat.color+'44',color:cat.color}}>
                        {cat.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-800 font-medium line-clamp-1">{item.description}</p>
                      {item.notes && <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{item.notes}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {(isExp?item.beneficiary:item.source)||<span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-bold whitespace-nowrap ${isExp?'text-red-600':'text-emerald-600'}`}>
                        {fmt(item.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={()=>openDetail(item,isExp?'depense':'recette')}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Voir détails">
                          <Eye className="w-4 h-4"/>
                        </button>
                        {canManageExpenses ? (
                          <button onClick={()=>isExp?openEditExp(item):openEditRev(item)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" title="Modifier">
                            <Edit3 className="w-4 h-4"/>
                          </button>
                        ) : (
                          <button disabled className="p-1.5 text-gray-200 cursor-not-allowed rounded-lg"><Edit3 className="w-4 h-4"/></button>
                        )}
                        {canDelete ? (
                          <button onClick={()=>isExp?setDeleteExpTarget({id:item.id,desc:item.description}):setDeleteRevTarget({id:item.id,desc:item.description})}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer">
                            <Trash2 className="w-4 h-4"/>
                          </button>
                        ) : (
                          <button disabled className="p-1.5 text-gray-200 cursor-not-allowed rounded-lg"><Trash2 className="w-4 h-4"/></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              {activeList.length===0?'Aucune entrée':`${(page-1)*PER_PAGE+1}–${Math.min(page*PER_PAGE,activeList.length)} sur ${activeList.length}`}
            </span>
            <select value={PER_PAGE} onChange={e=>{setPERPAGE(+e.target.value);setPage(1);}}
              className="px-2 py-1 border border-gray-300 rounded text-xs bg-white focus:ring-1 focus:ring-blue-500">
              {[10,15,25,50].map(n=><option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={()=>setPage(1)} disabled={page===1} className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronsLeft className="w-3.5 h-3.5"/></button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft className="w-3.5 h-3.5"/></button>
            {Array.from({length:Math.min(totalPages,5)},(_,i)=>{
              let n=i+1;
              if(totalPages>5){
                if(page<=3) n=i+1;
                else if(page>=totalPages-2) n=totalPages-4+i;
                else n=page-2+i;
              }
              return <button key={n} onClick={()=>setPage(n)}
                className={`min-w-[30px] h-[30px] text-xs rounded-lg border font-medium transition-all
                  ${n===page?'bg-blue-600 text-white border-blue-600':'border-gray-300 text-gray-600 hover:bg-white hover:border-blue-300'}`}>{n}</button>;
            })}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages||totalPages===0} className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-3.5 h-3.5"/></button>
            <button onClick={()=>setPage(totalPages)} disabled={page===totalPages||totalPages===0} className="p-1.5 border border-gray-300 rounded-lg text-gray-500 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed"><ChevronsRight className="w-3.5 h-3.5"/></button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Page</span>
            <input type="number" min={1} max={totalPages||1} value={page}
              onChange={e=>{const v=Math.max(1,Math.min(totalPages||1,+e.target.value));if(!isNaN(v))setPage(v);}}
              className="w-12 px-2 py-1 border border-gray-300 rounded text-xs text-center focus:ring-1 focus:ring-blue-500"/>
            <span className="text-xs text-gray-400">/ {totalPages||1}</span>
          </div>
        </div>
      </div>

      {/* Bilan récap */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h4 className="text-sm font-bold text-gray-800 mb-3">Top dépenses</h4>
          {catData.length===0?<p className="text-gray-400 text-xs">Aucune dépense</p>:
            catData.slice(0,5).map((c,i)=>(
              <div key={i} className="mb-2.5">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{c.label}</span>
                  <span className="text-gray-500 font-medium">{fmt(c.total)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{width:`${catData[0]?.total>0?(c.total/catData[0].total)*100:0}%`,background:c.color}}/>
                </div>
              </div>
            ))
          }
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h4 className="text-sm font-bold text-gray-800 mb-3">Top recettes</h4>
          {revCatData.length===0?<p className="text-gray-400 text-xs">Aucune recette</p>:
            revCatData.slice(0,5).map((c,i)=>(
              <div key={i} className="mb-2.5">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600">{c.label}</span>
                  <span className="text-emerald-600 font-medium">{fmt(c.total)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{width:`${revCatData[0]?.total>0?(c.total/revCatData[0].total)*100:0}%`,background:c.color}}/>
                </div>
              </div>
            ))
          }
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h4 className="text-sm font-bold text-gray-800 mb-3">Synthèse {yearFilter}</h4>
          <div className="space-y-2">
            {[
              {label:'⚡ Rev. Total Énergie', val:fmt(totalEnergie),  color:'text-blue-600'},
              {label:'➕ Recettes ajoutées',   val:fmt(totalRecettes), color:'text-emerald-600'},
              {label:'💰 Revenu Total',        val:fmt(totalRevenue),  color:'text-purple-700', bold:true},
              {label:'💸 Dépenses',            val:fmt(totalExpenses), color:'text-red-600'},
              {label:'📊 Solde Net',           val:fmt(netBalance),    color:netBalance>=0?'text-emerald-700':'text-orange-600', bold:true},
              {label:'📈 Ratio dép./rev.',     val:`${expenseRatio.toFixed(1)}%`, color:expenseRatio<30?'text-emerald-600':expenseRatio<60?'text-yellow-600':'text-red-600'},
            ].map((r,i)=>(
              <div key={i} className="flex justify-between items-center py-1.5 border-b border-gray-50 last:border-0">
                <span className="text-xs text-gray-500">{r.label}</span>
                <span className={`text-xs font-bold ${r.color}`}>{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ MODAL DÉPENSE ═══ */}
      {showExpForm && canManageExpenses && (
        <ModalForm title={editExpTarget?'Modifier la dépense':'Nouvelle dépense'}
          icon={<TrendingDown className="w-4 h-4 text-red-500"/>} accentColor="blue"
          onClose={()=>setShowExpForm(false)} onSave={handleSaveExp} saving={saving} isEdit={!!editExpTarget}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input type="date" value={expForm.date} onChange={e=>setExpForm({...expForm,date:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie *</label>
              <select value={expForm.category} onChange={e=>setExpForm({...expForm,category:e.target.value,customCategory:''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                {CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {expForm.category==='autre' && (
                <input type="text" placeholder="Précisez..." value={expForm.customCategory}
                  onChange={e=>setExpForm({...expForm,customCategory:e.target.value})}
                  className="w-full mt-2 px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-purple-50" autoFocus/>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
            <input type="text" placeholder="Ex: Transport technicien..." value={expForm.description}
              onChange={e=>setExpForm({...expForm,description:e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bénéficiaire</label>
              <input type="text" placeholder="Nom / fournisseur" value={expForm.beneficiary}
                onChange={e=>setExpForm({...expForm,beneficiary:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Montant (FCFA) *</label>
              <input type="number" min="0" step="100" placeholder="0" value={expForm.amount}
                onChange={e=>setExpForm({...expForm,amount:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Justificatifs</label>
            <textarea rows={2} value={expForm.notes} onChange={e=>setExpForm({...expForm,notes:e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          {+expForm.amount>0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <Info className="w-4 h-4 flex-shrink-0"/>
              <span className="font-medium">{fmt(+expForm.amount)}</span> enregistrés comme dépense
            </div>
          )}
        </ModalForm>
      )}

      {/* ═══ MODAL RECETTE ═══ */}
      {showRevForm && canManageExpenses && (
        <ModalForm title={editRevTarget?'Modifier la recette':'Nouvelle recette'}
          icon={<TrendingUp className="w-4 h-4 text-emerald-600"/>} accentColor="emerald"
          onClose={()=>setShowRevForm(false)} onSave={handleSaveRev} saving={saving} isEdit={!!editRevTarget}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
              <input type="date" value={revForm.date} onChange={e=>setRevForm({...revForm,date:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Catégorie *</label>
              <select value={revForm.category} onChange={e=>setRevForm({...revForm,category:e.target.value,customCategory:''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500">
                {REVENUE_CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              {revForm.category==='autre' && (
                <input type="text" placeholder="Précisez..." value={revForm.customCategory}
                  onChange={e=>setRevForm({...revForm,customCategory:e.target.value})}
                  className="w-full mt-2 px-3 py-2 border border-purple-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 bg-purple-50" autoFocus/>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description *</label>
            <input type="text" placeholder="Ex: Vente abonnement client..." value={revForm.description}
              onChange={e=>setRevForm({...revForm,description:e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Source / Client</label>
              <input type="text" placeholder="Nom du client / source" value={revForm.source}
                onChange={e=>setRevForm({...revForm,source:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Montant (FCFA) *</label>
              <input type="number" min="0" step="100" placeholder="0" value={revForm.amount}
                onChange={e=>setRevForm({...revForm,amount:e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Justificatifs</label>
            <textarea rows={2} value={revForm.notes} onChange={e=>setRevForm({...revForm,notes:e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-emerald-500"/>
          </div>
          {+revForm.amount>0 && (
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
              <Info className="w-4 h-4 flex-shrink-0"/>
              <span className="font-medium">{fmt(+revForm.amount)}</span> ajoutés au Revenu Total
            </div>
          )}
        </ModalForm>
      )}

      {/* ═══ MODAL DÉTAILS ═══ */}
      {showDetail && detailItem && (() => {
        const isExp = detailType === 'depense';
        const cat   = isExp ? getCat(detailItem.category) : getRevCat(detailItem.category);
        return (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {/* ✅ flex-col + max-h-[90vh] pour scroll interne */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 flex flex-col max-h-[90vh]">

              {/* Header — fixe, ne scroll pas */}
              <div className="rounded-t-2xl p-5 flex-shrink-0" style={{background:`linear-gradient(135deg,${cat.color}18,${cat.color}08)`,borderBottom:`2px solid ${cat.color}30`}}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-sm" style={{background:cat.color+'22',border:`1px solid ${cat.color}44`}}>
                      {cat.label.split(' ')[0]}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">Détail {isExp?'dépense':'recette'}</h3>
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{background:cat.color+'22',color:cat.color}}>{cat.label}</span>
                    </div>
                  </div>
                  <button onClick={()=>setShowDetail(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-4 h-4"/></button>
                </div>
              </div>

              {/* Body — scrollable */}
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className={`text-center py-3 rounded-xl border ${isExp?'bg-red-50 border-red-100':'bg-emerald-50 border-emerald-100'}`}>
                  <p className={`text-xs font-medium mb-1 ${isExp?'text-red-500':'text-emerald-500'}`}>MONTANT</p>
                  <p className={`text-3xl font-extrabold ${isExp?'text-red-600':'text-emerald-600'}`}>{isExp?'−':'+'}{fmt(detailItem.amount)}</p>
                </div>
                <div className="space-y-3">
                  {[
                    {icon:<Calendar className="w-4 h-4 text-gray-400"/>, label:'Date', value: new Date(detailItem.date).toLocaleDateString('fr-FR',{weekday:'long',year:'numeric',month:'long',day:'numeric'})},
                    {icon:<AlignLeft className="w-4 h-4 text-gray-400"/>, label:'Description', value: detailItem.description},
                    (isExp?detailItem.beneficiary:detailItem.source) && {icon:<User className="w-4 h-4 text-gray-400"/>, label:isExp?'Bénéficiaire':'Source', value:isExp?detailItem.beneficiary:detailItem.source},
                    {icon:<AlignLeft className="w-4 h-4 text-gray-400"/>, label:'Justificatifs', value: detailItem.notes || <span className="text-gray-300 italic">Aucune note</span>},
                    {icon:<User className="w-4 h-4 text-gray-400"/>, label:'Saisi par', value: detailItem.created_by||'Inconnu'},
                    detailItem.created_at && {icon:<Clock className="w-4 h-4 text-gray-400"/>, label:'Créé le', value: new Date(detailItem.created_at).toLocaleString('fr-FR')},
                    {icon:<Hash className="w-4 h-4 text-gray-400"/>, label:'ID', value: <span className="font-mono text-xs">{detailItem.id}</span>},
                  ].filter(Boolean).map((item,i)=>(
                    <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                      <div className="mt-0.5 flex-shrink-0">{item.icon}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{item.label}</p>
                        <p className="text-sm text-gray-800 mt-0.5 break-words">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer — fixe, toujours visible */}
              <div className="flex gap-3 p-5 border-t border-gray-100 flex-shrink-0">
                <button onClick={()=>setShowDetail(false)} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium">Fermer</button>
                {canManageExpenses && (
                  <button onClick={()=>{setShowDetail(false);isExp?openEditExp(detailItem):openEditRev(detailItem);}}
                    className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
                    <Edit3 className="w-4 h-4"/>Modifier
                  </button>
                )}
                {canDelete && (
                  <button onClick={()=>{setShowDetail(false);isExp?setDeleteExpTarget({id:detailItem.id,desc:detailItem.description}):setDeleteRevTarget({id:detailItem.id,desc:detailItem.description});}}
                    className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center">
                    <Trash2 className="w-4 h-4"/>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ CONFIRM DELETE ═══ */}
      {(deleteExpTarget||deleteRevTarget) && (() => {
        const target  = deleteExpTarget||deleteRevTarget;
        const isExp   = !!deleteExpTarget;
        const confirm = isExp?confirmDeleteExp:confirmDeleteRev;
        const cancel  = ()=>isExp?setDeleteExpTarget(null):setDeleteRevTarget(null);
        return (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            {/* ✅ flex-col + max-h-[90vh] pour scroll interne */}
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 overflow-hidden flex flex-col max-h-[90vh]">
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-5 flex items-center gap-4 flex-shrink-0">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-white"/>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Confirmer la suppression</h3>
                  <p className="text-red-100 text-sm">Suppression {isExp?'dépense':'recette'} — irréversible</p>
                </div>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <p className="text-gray-600 text-sm mb-3">Vous allez supprimer définitivement :</p>
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                  <p className="text-red-800 font-semibold text-sm line-clamp-2">« {target.desc} »</p>
                </div>
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5"/>
                  <span>Cette entrée sera supprimée définitivement et le Revenu Total sera recalculé.</span>
                </div>
              </div>
              <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
                <button onClick={cancel} className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 text-sm font-semibold">Annuler</button>
                <button onClick={confirm} className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 shadow-md">
                  <Trash2 className="w-4 h-4"/>Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

// ─── ModalForm réutilisable ───────────────────────────────
// CORRIGÉ : flex-col + max-h-[90vh] + overflow-y-auto sur le body + footer fixe
function ModalForm({ title, icon, accentColor, onClose, onSave, saving, isEdit, children }) {
  const btn = accentColor==='emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700';
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-gray-200 flex flex-col max-h-[90vh]">
        {/* Header fixe */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">{icon}{title}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4"/></button>
        </div>
        {/* Body scrollable */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">{children}</div>
        {/* Footer fixe */}
        <div className="flex gap-3 p-5 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 text-sm font-medium">Annuler</button>
          <button onClick={onSave} disabled={saving}
            className={`flex-1 px-4 py-2.5 disabled:opacity-50 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${btn}`}>
            {saving?<><Loader className="w-4 h-4 animate-spin"/>Enregistrement...</>:<><Save className="w-4 h-4"/>{isEdit?'Modifier':'Ajouter'}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Comptabilite;